import { Link, Navigate, useSearchParams } from 'react-router'
import {
  TopBar,
  AppLayout,
  Mtds,
  PlusIcon,
  ScreenHeader,
  ScreenLayout,
  toUserMessage,
  useGetNewsArticlesQuery,
  useAuth
} from 'prove-common'
import { AppLogo } from '../components/app-logo/AppLogo'
import { SideBarLayout } from '../components/side-bar-layout/SideBarLayout'
import NewsArticleCard from '../components/news-article-card/NewsArticleCard'

export const NewsScreen = () => {
  const { isAdmin } = useAuth()
  return isAdmin ? <NewsList /> : <Navigate to='/unauthorized' replace />
}

function NewsList() {
  const [params, setParams] = useSearchParams()
  const {
    data: articles,
    isLoading,
    isError,
    error,
    refetch
  } = useGetNewsArticlesQuery()
  const q = params.get('q') ?? ''
  const app = params.get('app') ?? ''
  const type = params.get('type') ?? ''
  const search = params.toString() ? `?${params.toString()}` : ''
  const filtered = (articles ?? [])
    .filter(
      (article) =>
        (!q.trim() ||
          article.subject
            .toLocaleLowerCase('nb-NO')
            .includes(q.trim().toLocaleLowerCase('nb-NO'))) &&
        (!app || article.app === app) &&
        (!type || article.icon === type)
    )
    .sort((a, b) => Date.parse(b.date) - Date.parse(a.date) || b.id - a.id)

  const updateFilter = (key: string, value: string) => {
    setParams(
      (current) => {
        const next = new URLSearchParams(current)
        if (value) next.set(key, value)
        else next.delete(key)
        return next
      },
      { replace: key === 'q' }
    )
  }

  return (
    <AppLayout
      sidebar={<SideBarLayout />}
      useMobileBar={false}
      isFullWidth
      topBar={<TopBar logo={<AppLogo />} title='Nyheter' />}
    >
      <ScreenLayout
        header={
          <ScreenHeader
            title='Nyhetsartikler'
            actions={
              <Mtds.Button
                as={Link}
                to={`/news/new${search}`}
                data-variant='primary'
              >
                <PlusIcon />
                Ny artikkel
              </Mtds.Button>
            }
          />
        }
      >
        <Mtds.Grid data-gap='6'>
          <Mtds.Muted>
            Administrer nyheter for PrøveTa og PrøvePlan. Lagring gjør nyheter
            tilgjengelige med en gang, frem til de utløper.
          </Mtds.Muted>
          <Mtds.Grid data-items='250' data-gap='4'>
            <Mtds.Field
              as='input'
              type='search'
              label='Søk i nyheter'
              value={q}
              onChange={(event) => updateFilter('q', event.target.value)}
            />
            <Mtds.Field
              as='select'
              label='App'
              value={app}
              onChange={(event) => updateFilter('app', event.target.value)}
              options={[
                { value: '', label: 'Alle apper' },
                { value: 'ProveTa', label: 'PrøveTa' },
                { value: 'ProvePlan', label: 'PrøvePlan' },
                ...(!['', 'ProveTa', 'ProvePlan'].includes(app)
                  ? [{ value: app, label: app }]
                  : [])
              ]}
            />
            <Mtds.Field
              as='select'
              label='Type'
              value={type}
              onChange={(event) => updateFilter('type', event.target.value)}
              options={[
                { value: '', label: 'Alle typer' },
                { value: 'merge', label: 'Nye funksjoner' },
                { value: 'bug', label: 'Feilretting' },
                ...(!['', 'merge', 'bug'].includes(type)
                  ? [{ value: type, label: type }]
                  : [])
              ]}
            />
          </Mtds.Grid>
          {(q || app || type) && (
            <Mtds.Flex>
              <Mtds.Button
                type='button'
                data-variant='secondary'
                onClick={() =>
                  setParams((current) => {
                    const next = new URLSearchParams(current)
                    for (const key of ['q', 'app', 'type']) next.delete(key)
                    return next
                  })
                }
              >
                Nullstill søk og filtre
              </Mtds.Button>
            </Mtds.Flex>
          )}
          {isLoading ? (
            <p role='status'>Laster nyhetsartikler …</p>
          ) : isError ? (
            <Mtds.Alert as='div' role='alert' data-color='danger'>
              <Mtds.Heading as='h2' data-size='sm'>
                Kunne ikke hente nyhetene
              </Mtds.Heading>
              <p>{toUserMessage(error)}</p>
              <Mtds.Button
                type='button'
                data-variant='secondary'
                onClick={() => void refetch()}
              >
                Prøv igjen
              </Mtds.Button>
            </Mtds.Alert>
          ) : (
            <>
              <Mtds.Muted role='status'>
                {filtered.length} av {articles?.length ?? 0} artikler
              </Mtds.Muted>
              {filtered.length ? (
                <Mtds.Grid data-gap='3'>
                  {filtered.map((article) => (
                    <NewsArticleCard
                      key={article.id}
                      article={article}
                      detailTo={`/news/${article.id}${search}`}
                      editTo={`/news/${article.id}/edit${search}`}
                    />
                  ))}
                </Mtds.Grid>
              ) : (
                <Mtds.Group>
                  <Mtds.Prose>
                    <Mtds.Heading as='h2' data-size='sm'>
                      {articles?.length
                        ? 'Ingen treff'
                        : 'Ingen nyhetsartikler ennå'}
                    </Mtds.Heading>
                    <p>
                      {articles?.length
                        ? 'Prøv et annet søk eller nullstill filtrene.'
                        : 'Opprett en artikkel for å dele nyheter med brukerne.'}
                    </p>
                  </Mtds.Prose>
                </Mtds.Group>
              )}
            </>
          )}
        </Mtds.Grid>
      </ScreenLayout>
    </AppLayout>
  )
}
